/**
 * MatchModal - Create/Edit match wizard (3 steps)
 *
 * Steps:
 * 1. Match Details - Basic info, teams, assignments
 * 2. FTP Destinations - Select FTP targets for photo delivery
 * 3. IPTC Configuration - Metadata templates for exported photos
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import Modal from '../../../../shared/components/ui/Modal';
import Button from '../../../../shared/components/ui/Button';
import Stepper from '../../../../shared/components/ui/Stepper/Stepper';
import { EVENT_TYPES, COMPETITIONS, SPORTS } from '../../utils/constants';
import { lookupVenueLocation } from '../../utils/venueLocations';
import FtpSelectionStep from './FtpSelectionStep';
import IptcConfigStep from './IptcConfigStep';
import styles from './MatchModal.module.css';

/**
 * Search input with debounce for MatchHub lookup
 */
function MatchHubSearch({ onSelect, onManualEntry }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Debounced search - in real implementation, this would call the MatchHub API
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setSearched(true);
      try {
        // TODO: Replace with actual MatchHub API call
        // const data = await fetchMatchHubMatches(query);
        // setResults(data);

        // Stub: simulate empty results for now
        setResults([]);
      } catch (err) {
        console.error('MatchHub search failed:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className={styles.matchHubSearch}>
      <div className={styles.searchHeader}>
        <label className={styles.label}>Search MatchHub</label>
        <span className={styles.hint}>Find matches from external data</span>
      </div>
      <input
        type="text"
        className={styles.searchInput}
        placeholder="Search by team, competition, or date..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {loading && <div className={styles.searchLoading}>Searching...</div>}

      {results.length > 0 && (
        <div className={styles.searchResults}>
          {results.map((match) => (
            <button
              key={match.id}
              className={styles.searchResult}
              onClick={() => onSelect(match)}
            >
              <span className={styles.resultTeams}>
                {match.home_team} vs {match.away_team}
              </span>
              <span className={styles.resultMeta}>
                {match.competition} - {match.date}
              </span>
            </button>
          ))}
        </div>
      )}

      {searched && !loading && results.length === 0 && query.trim() && (
        <div className={styles.noResults}>
          <p>No matches found in MatchHub</p>
          <Button variant="ghost" size="sm" onClick={onManualEntry}>
            Enter match manually
          </Button>
        </div>
      )}

      <div className={styles.divider}>
        <span>or</span>
      </div>

      <Button variant="ghost" fullWidth onClick={onManualEntry}>
        Enter match details manually
      </Button>
    </div>
  );
}

/**
 * Searchable team selector with option to create new
 */
function TeamSelector({ label, value, onChange, teams, onCreateTeam }) {
  const [showCreate, setShowCreate] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedTeam = teams.find((t) => String(t.id) === String(value));

  const filteredTeams = searchQuery.trim()
    ? teams.filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 10)
    : teams.slice(0, 10);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (teamId) => {
    onChange(teamId ? String(teamId) : null);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleCreate = async () => {
    if (newTeamName.trim()) {
      const newTeam = await onCreateTeam(newTeamName.trim());
      setNewTeamName('');
      setShowCreate(false);
      if (newTeam) {
        onChange(String(newTeam.id));
      }
    }
  };

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      {!showCreate ? (
        <div className={styles.teamSelectRow} ref={dropdownRef}>
          <div className={styles.teamSearchWrapper}>
            <input
              type="text"
              className={styles.input}
              placeholder="Search team..."
              value={isOpen ? searchQuery : (selectedTeam?.name || '')}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => {
                setIsOpen(true);
                setSearchQuery('');
              }}
            />
            {value && !isOpen && (
              <button
                type="button"
                className={styles.teamClearButton}
                onClick={() => handleSelect(null)}
                title="Clear selection"
              >
                x
              </button>
            )}
            {isOpen && (
              <div className={styles.teamDropdown}>
                {filteredTeams.length === 0 ? (
                  <div className={styles.teamDropdownEmpty}>No teams found</div>
                ) : (
                  filteredTeams.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      className={styles.teamDropdownItem}
                      onClick={() => handleSelect(team.id)}
                    >
                      {team.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCreate(true)}
            title="Add new team"
          >
            +
          </Button>
        </div>
      ) : (
        <div className={styles.createTeamRow}>
          <input
            type="text"
            className={styles.input}
            placeholder="New team name..."
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            autoFocus
          />
          <Button variant="primary" size="sm" onClick={handleCreate}>
            Add
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select checkbox list for user assignments
 */
function UserCheckboxList({ label, users, selectedIds, onChange, autoSelectedHint }) {
  const handleToggle = (userId) => {
    const idStr = String(userId);
    if (selectedIds.includes(idStr)) {
      onChange(selectedIds.filter((id) => id !== idStr));
    } else {
      onChange([...selectedIds, idStr]);
    }
  };

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      <div className={styles.checkboxList}>
        {users.length === 0 ? (
          <span className={styles.noUsers}>No users available</span>
        ) : (
          users.map((user) => (
            <label key={user.id} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={selectedIds.includes(String(user.id))}
                onChange={() => handleToggle(user.id)}
                className={styles.checkbox}
              />
              <span className={styles.checkboxLabel}>{user.username || user.name}</span>
            </label>
          ))
        )}
      </div>
      {autoSelectedHint && (
        <span className={styles.autoFilled}>{autoSelectedHint}</span>
      )}
    </div>
  );
}

/**
 * Step 1: Match details form
 */
function MatchDetailsForm({
  formData,
  onChange,
  teams,
  photographers,
  editors,
  onCreateTeam,
  errors,
}) {
  const updateField = useCallback((field, value) => {
    onChange((prev) => ({ ...prev, [field]: value }));
  }, [onChange]);

  // Handle venue change with location auto-suggest
  const handleVenueChange = useCallback((venue) => {
    const location = lookupVenueLocation(venue);
    onChange((prev) => ({
      ...prev,
      venue,
      // Only auto-fill if location fields are empty (don't overwrite user edits)
      city: prev.city || location?.city || '',
      state: prev.state || location?.state || '',
      country: prev.country || location?.country || '',
      country_code: prev.country_code || location?.countryCode || '',
    }));
  }, [onChange]);

  // Auto-select photographer if only one available
  useEffect(() => {
    if (photographers.length === 1 && formData.photographer_ids.length === 0) {
      updateField('photographer_ids', [String(photographers[0].id)]);
    }
  }, [photographers, formData.photographer_ids, updateField]);

  return (
    <div className={styles.form}>
      {/* Event Type */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Event Type</label>
        <select
          className={styles.select}
          value={formData.event_type || 'match'}
          onChange={(e) => updateField('event_type', e.target.value)}
        >
          {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Teams (only for match type) */}
      {formData.event_type === 'match' && (
        <div className={styles.formRow}>
          <TeamSelector
            label="Home Team *"
            value={formData.home_team}
            onChange={(v) => updateField('home_team', v)}
            teams={teams}
            onCreateTeam={onCreateTeam}
          />
          <TeamSelector
            label="Away Team *"
            value={formData.away_team}
            onChange={(v) => updateField('away_team', v)}
            teams={teams}
            onCreateTeam={onCreateTeam}
          />
        </div>
      )}

      {/* Event Name */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {formData.event_type === 'match' ? 'Match Name (optional)' : 'Event Name *'}
        </label>
        <input
          type="text"
          className={styles.input}
          placeholder={formData.event_type === 'match' ? 'e.g., Eredivisie Speelronde 21' : 'Event name...'}
          value={formData.name || ''}
          onChange={(e) => updateField('name', e.target.value)}
        />
        {errors?.name && <span className={styles.error}>{errors.name}</span>}
      </div>

      {/* Date/Time */}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Start Time *</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.start_time || ''}
            onChange={(e) => updateField('start_time', e.target.value)}
          />
          {errors?.start_time && <span className={styles.error}>{errors.start_time}</span>}
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>End Time</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.end_time || ''}
            onChange={(e) => updateField('end_time', e.target.value)}
          />
        </div>
      </div>

      {/* Venue and Location */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Venue</label>
        <input
          type="text"
          className={styles.input}
          placeholder="De Kuip"
          value={formData.venue || ''}
          onChange={(e) => handleVenueChange(e.target.value)}
        />
        <span className={styles.hint}>Location fields auto-fill based on venue</span>
      </div>

      {/* Location Fields */}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>City</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Rotterdam"
            value={formData.city || ''}
            onChange={(e) => updateField('city', e.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>State/Province</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Zuid-Holland"
            value={formData.state || ''}
            onChange={(e) => updateField('state', e.target.value)}
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country</label>
          <input
            type="text"
            className={styles.input}
            placeholder="The Netherlands"
            value={formData.country || ''}
            onChange={(e) => updateField('country', e.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country Code</label>
          <input
            type="text"
            className={styles.input}
            placeholder="NLD"
            value={formData.country_code || ''}
            onChange={(e) => updateField('country_code', e.target.value)}
            maxLength={3}
          />
        </div>
      </div>

      {/* Competition (for matches) */}
      {formData.event_type === 'match' && (
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Competition</label>
            <select
              className={styles.select}
              value={formData.competition || ''}
              onChange={(e) => updateField('competition', e.target.value || null)}
            >
              <option value="">Select competition...</option>
              {Object.entries(COMPETITIONS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Sport</label>
            <select
              className={styles.select}
              value={formData.sport || 'football'}
              onChange={(e) => updateField('sport', e.target.value)}
            >
              {Object.entries(SPORTS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Assignments */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionTitle}>Assignments</h4>
        <div className={styles.formRow}>
          <UserCheckboxList
            label="Photographers"
            users={photographers}
            selectedIds={formData.photographer_ids}
            onChange={(ids) => updateField('photographer_ids', ids)}
            autoSelectedHint={photographers.length === 1 ? 'Auto-selected (only photographer)' : null}
          />
          <UserCheckboxList
            label="Editors"
            users={editors}
            selectedIds={formData.editor_ids}
            onChange={(ids) => updateField('editor_ids', ids)}
          />
        </div>
      </div>
    </div>
  );
}

// Default IPTC config
const DEFAULT_IPTC_CONFIG = {
  title_template: '',
  headline_template: '',
  caption_template: '',
  city: '',
  state: '',
  country: '',
  country_code: '',
  location: '',
  event_name: '',
  keywords: [],
  credit: '',
  copyright_notice: '',
  category: 'S',
  supplemental_categories: ['SPO', 'SOC', 'FOC'],
};

/**
 * Main MatchModal component - 3-step wizard
 */
export default function MatchModal({
  isOpen,
  onClose,
  onSave,
  match = null, // null for create, object for edit
  teams = [],
  photographers = [],
  editors = [],
  onCreateTeam,
  loading = false,
  // API functions for FTP and IPTC
  fetchFtpTargets,
  fetchCompanyDefaults,
  fetchIptcPreview,
}) {
  const [mode, setMode] = useState('search'); // 'search' | 'manual'
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    event_type: 'match',
    name: '',
    home_team: null,
    away_team: null,
    start_time: '',
    end_time: '',
    venue: '',
    city: '',
    state: '',
    country: '',
    country_code: '',
    competition: null,
    sport: 'football',
    photographer_ids: [],
    editor_ids: [],
  });
  const [ftpTargetIds, setFtpTargetIds] = useState([]);
  const [iptcConfig, setIptcConfig] = useState(DEFAULT_IPTC_CONFIG);
  const [errors, setErrors] = useState({});

  const isEdit = !!match;

  // Initialize form data when editing - using a helper function to compute values
  // and then calling setters. This is intentional state sync from props.
  useEffect(() => {
    // Compute all new values first
    const initializeFromMatch = () => {
      if (match) {
        const photographerIds = (match.photographer_assignments || [])
          .map((a) => String(a.user_id));
        const editorIds = (match.editor_assignments || [])
          .map((a) => String(a.user_id));
        const ftpIds = (match.ftp_target_assignments || [])
          .map((a) => String(a.ftp_target_id));

        return {
          formData: {
            event_type: match.event_type || 'match',
            name: match.name || '',
            home_team: match.home_team || null,
            away_team: match.away_team || null,
            start_time: match.start_time ? match.start_time.slice(0, 16) : '',
            end_time: match.end_time ? match.end_time.slice(0, 16) : '',
            venue: match.venue || '',
            city: match.iptc_config?.city || '',
            state: match.iptc_config?.state || '',
            country: match.iptc_config?.country || '',
            country_code: match.iptc_config?.country_code || '',
            competition: match.competition || null,
            sport: match.sport || 'football',
            photographer_ids: photographerIds,
            editor_ids: editorIds,
          },
          ftpTargetIds: ftpIds,
          iptcConfig: match.iptc_config
            ? { ...DEFAULT_IPTC_CONFIG, ...match.iptc_config }
            : DEFAULT_IPTC_CONFIG,
          mode: 'manual',
          currentStep: 1,
        };
      }
      return {
        formData: null, // Keep existing
        ftpTargetIds: [],
        iptcConfig: DEFAULT_IPTC_CONFIG,
        mode: 'search',
        currentStep: 1,
      };
    };

    const values = initializeFromMatch();

    // Apply state updates (intentional sync from props on modal open)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (values.formData) setFormData(values.formData);
    setFtpTargetIds(values.ftpTargetIds);
    setIptcConfig(values.iptcConfig);
    setMode(values.mode);
    setCurrentStep(values.currentStep);
  }, [match, isOpen]);

  // Validate step 1
  const validateStep1 = useCallback(() => {
    const errs = {};

    if (formData.event_type === 'match') {
      if (!formData.home_team) errs.home_team = 'Required';
      if (!formData.away_team) errs.away_team = 'Required';
    } else {
      if (!formData.name?.trim()) errs.name = 'Required';
    }

    if (!formData.start_time) errs.start_time = 'Required';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  // Navigate to next step
  const handleNext = () => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  // Navigate to previous step
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Handle save
  const handleSave = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    const data = {
      ...formData,
      start_time: new Date(formData.start_time).toISOString(),
      end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null,
      // Convert string IDs to appropriate format for the API
      photographer_ids: formData.photographer_ids,
      editor_ids: formData.editor_ids,
      ftp_target_ids: ftpTargetIds,
      iptc_config: iptcConfig,
    };

    await onSave(data);
  };

  // Handle MatchHub selection
  const handleMatchHubSelect = (hubMatch) => {
    setFormData({
      ...formData,
      name: hubMatch.name || '',
      home_team: hubMatch.home_team_id || null,
      away_team: hubMatch.away_team_id || null,
      start_time: hubMatch.start_time?.slice(0, 16) || '',
      venue: hubMatch.venue || '',
      competition: hubMatch.competition || null,
    });
    setMode('manual');
  };

  // Render current step content
  const renderStepContent = () => {
    if (mode === 'search' && !isEdit && currentStep === 1) {
      return (
        <MatchHubSearch
          onSelect={handleMatchHubSelect}
          onManualEntry={() => setMode('manual')}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <MatchDetailsForm
            formData={formData}
            onChange={setFormData}
            teams={teams}
            photographers={photographers}
            editors={editors}
            onCreateTeam={onCreateTeam}
            errors={errors}
          />
        );
      case 2:
        return (
          <FtpSelectionStep
            selectedIds={ftpTargetIds}
            onChange={setFtpTargetIds}
            fetchFtpTargets={fetchFtpTargets}
          />
        );
      case 3:
        return (
          <IptcConfigStep
            config={iptcConfig}
            onChange={setIptcConfig}
            matchData={formData}
            teams={teams}
            fetchCompanyDefaults={fetchCompanyDefaults}
            fetchPreview={fetchIptcPreview}
          />
        );
      default:
        return null;
    }
  };

  // Step labels
  const stepLabels = ['Match Details', 'FTP Destinations', 'IPTC Config'];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        {isEdit ? 'Edit Match' : 'Create Match'}
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {/* Stepper - only show in manual mode */}
        {mode === 'manual' && (
          <Stepper currentStep={currentStep}>
            <Stepper.Step number={1} label={stepLabels[0]} />
            <Stepper.Step number={2} label={stepLabels[1]} />
            <Stepper.Step number={3} label={stepLabels[2]} />
          </Stepper>
        )}

        {renderStepContent()}
      </Modal.Body>

      <Modal.Footer>
        {/* Back button */}
        {mode === 'manual' && currentStep > 1 && (
          <Button variant="ghost" onClick={handleBack}>
            Back
          </Button>
        )}

        {/* Back to search button */}
        {mode === 'manual' && currentStep === 1 && !isEdit && (
          <Button variant="ghost" onClick={() => setMode('search')}>
            Back to Search
          </Button>
        )}

        <div style={{ flex: 1 }} />

        {/* Cancel button */}
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>

        {/* Next/Create button */}
        {mode === 'manual' && (
          <>
            {currentStep < 3 ? (
              <Button variant="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSave} loading={loading}>
                {isEdit ? 'Save Changes' : 'Create Match'}
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
